import { useEffect, useRef, useState, useCallback, Fragment } from "react";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection, doc, addDoc, setDoc, onSnapshot,
  query, orderBy, limitToLast, increment,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import { registerUserFcmToken } from "../services/notificationService";
import { fmtChatTime, fmtChatDateSep, sameLocalDay } from "../utils/chatTime";
import "./SupportChat.css";

/**
 * TurantChat — in-app support chat (user ↔ ops).
 *
 * - Renders a floating launcher only for a logged-in user whose Firebase
 *   session matches their phone (rules: supportChats/{phone} is owner+admin).
 * - One thread per phone: supportChats/{phone} (summary) + /messages.
 * - Messages listener attaches only while the panel is open (limitToLast 50);
 *   the 1-doc summary listener powers the unread badge.
 * - Other components can open it (optionally prefilled) via:
 *     window.dispatchEvent(new CustomEvent("tx-chat-open", { detail: { prefill: "..." } }))
 * - No typing indicators / presence on purpose — they burn writes.
 */
export default function SupportChat() {
  const [phone, setPhone] = useState(null);      // set only when auth uid == stored phone
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [summary, setSummary] = useState(null); // supportChats/{phone} doc
  const [notifPerm, setNotifPerm] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const bodyRef = useRef(null);
  const openRef = useRef(false);
  const tokenSavedRef = useRef(false); // one silent token refresh per session
  openRef.current = open;

  const unread = summary?.unreadByUser || 0;

  // Auth gate — avoids the attach-before-session-restore permission race.
  // OTP login stores PHONE_NUMBER *after* Firebase sign-in fires, so we also
  // re-check on the "tx-login" event dispatched by OtpVerifyModal.
  useEffect(() => {
    const check = (user) => {
      const stored = localStorage.getItem("PHONE_NUMBER");
      setPhone(user && stored && user.uid === stored ? stored : null);
    };
    const unsub = onAuthStateChanged(auth, check);
    const onLogin = () => check(auth.currentUser);
    window.addEventListener("tx-login", onLogin);
    return () => { unsub(); window.removeEventListener("tx-login", onLogin); };
  }, []);

  // Unread badge + reply preview — 1-doc summary listener.
  useEffect(() => {
    if (!phone) return;
    const unsub = onSnapshot(
      doc(db, "supportChats", phone),
      (snap) => setSummary(snap.exists() ? snap.data() : null),
      (err) => console.warn("chat summary listener:", err.code)
    );
    return () => unsub();
  }, [phone]);

  // Messages — only while the panel is open.
  useEffect(() => {
    if (!phone || !open) return;
    const q = query(
      collection(db, "supportChats", phone, "messages"),
      orderBy("at"),
      limitToLast(50)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        requestAnimationFrame(() => {
          if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
        });
      },
      (err) => console.warn("chat messages listener:", err.code)
    );
    return () => unsub();
  }, [phone, open]);

  // Opening clears my unread counter.
  useEffect(() => {
    if (!phone || !open || unread === 0) return;
    setDoc(doc(db, "supportChats", phone), { unreadByUser: 0 }, { merge: true }).catch(() => {});
  }, [phone, open, unread]);

  // Freeze the page behind the sheet while the chat is open (mobile scroll fix).
  useEffect(() => {
    if (!open) return;
    document.body.classList.add("txchat-open");
    return () => document.body.classList.remove("txchat-open");
  }, [open]);

  // Let any page open the chat (optionally prefilled) without prop drilling.
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.prefill) setInput(e.detail.prefill);
      setOpen(true);
    };
    window.addEventListener("tx-chat-open", handler);
    return () => window.removeEventListener("tx-chat-open", handler);
  }, []);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || !phone || sending) return;
    setSending(true);
    const at = new Date().toISOString();
    try {
      await addDoc(collection(db, "supportChats", phone, "messages"), { text, from: "user", at });
      await setDoc(doc(db, "supportChats", phone), {
        phone,
        lastMessage: text.slice(0, 120),
        lastFrom: "user",
        lastAt: at,
        status: "open",
        unreadByAdmin: increment(1),
        unreadByUser: 0,
      }, { merge: true });
      setInput("");
      // Permission already granted → refresh this device's token silently so
      // the "TurantX replied" push actually reaches them. Once per session.
      if (notifPerm === "granted" && !tokenSavedRef.current) {
        tokenSavedRef.current = true;
        registerUserFcmToken(phone);
      }
    } catch (err) {
      console.error("chat send failed:", err);
    } finally {
      setSending(false);
    }
  }, [input, phone, sending, notifPerm]);

  // "Get notified" strip — explicit tap asks for browser permission.
  const enableNotifs = useCallback(async () => {
    const token = await registerUserFcmToken(phone);
    if (token) tokenSavedRef.current = true;
    setNotifPerm(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  }, [phone]);

  if (!phone) return null; // logged-out users don't see the launcher

  return (
    <>
      {!open && (
        <>
          {unread > 0 && summary?.lastFrom === "admin" && (
            <button className="txchat-preview" onClick={() => setOpen(true)}>
              <span className="txchat-preview-title">💬 TurantX replied</span>
              <span className="txchat-preview-text">{summary?.lastMessage || ""}</span>
            </button>
          )}
          <button className="txchat-fab" onClick={() => setOpen(true)} aria-label="Open TurantChat support">
            💬
            {unread > 0 && <span className="txchat-fab-badge">{unread > 9 ? "9+" : unread}</span>}
          </button>
        </>
      )}

      {open && (
        <div className="txchat-panel" role="dialog" aria-label="TurantChat support">
          <div className="txchat-header">
            <div>
              <div className="txchat-title">TurantChat 💬</div>
              <div className="txchat-sub">TurantX Support · replies within ~30 min (9am–9pm)</div>
            </div>
            <button className="txchat-close" onClick={() => setOpen(false)} aria-label="Close chat">✕</button>
          </div>

          {notifPerm === "default" && (
            <button className="txchat-notif-strip" onClick={enableNotifs}>
              🔔 Get notified when we reply — <u>enable notifications</u>
            </button>
          )}

          <div className="txchat-body" ref={bodyRef}>
            {messages.length === 0 && (
              <div className="txchat-empty">
                Hi! 👋 Ask us anything about your order — pickup, delivery, OTP, anything.
              </div>
            )}
            {messages.map((m, i) => {
              const showSep = i === 0 || !sameLocalDay(messages[i - 1].at, m.at);
              return (
                <Fragment key={m.id}>
                  {showSep && <div className="txchat-datesep">{fmtChatDateSep(m.at)}</div>}
                  <div className={`txchat-bubble ${m.from === "user" ? "me" : "ops"}`}>
                    {m.text}
                    <span className="txchat-time">{fmtChatTime(m.at)}</span>
                  </div>
                </Fragment>
              );
            })}
          </div>

          <div className="txchat-inputbar">
            <input
              className="txchat-input"
              placeholder="Type your question…"
              value={input}
              maxLength={500}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
            />
            <button className="txchat-send" onClick={send} disabled={sending || !input.trim()} aria-label="Send">
              ➤
            </button>
          </div>
        </div>
      )}
    </>
  );
}
